import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ProgressBar from "~/components/ReverseEngineering/ProgressBar";
import { STATIC_PAGE_MESSAGES, TOAST_MESSAGES } from "~/constants/messages";
import { useOrganization } from "~/hooks/useOrganization";
import { useReverseEngineeringStatus } from "~/hooks/useReverseEngineeringStatus";
import type { ReverseEngineeringPhaseStatus, SelectedApp } from "~/interface/reverse";
import styles from "./index.module.scss";

interface ReportCreationStepProps {
  onComplete?: () => void;
  onAllTasksComplete?: () => void;
  onCompletionAvailabilityChange?: (available: boolean) => void;
  onClose?: () => void;
  selectedApps: SelectedApp[];
  dfInstanceId?: string;
  reportId?: string;
  organizationId?: string;
  projectId?: string;
  initialCheckpoint?: number | undefined;
}

interface LogEntry {
  time: string;
  message: string;
}

type PhaseStatus = "pending" | "running" | "completed" | "failed";

interface PhaseStates {
  dependency_analysis: PhaseStatus;
  er_diagram: PhaseStatus;
  requirements_analysis: PhaseStatus;
}

const PHASE_CHECKPOINTS = {
  dependency_analysis: 20,
  er_diagram: 40,
  requirements_analysis: 50,
} as const;

const PHASE_NAMES = {
  dependency_analysis: "依存関係分析",
  er_diagram: "ER図生成",
  requirements_analysis: "要件分析",
} as const;

/**
 * checkpointの値から初期のphaseStatesを計算する
 * - checkpoint < 20: 全てpending
 * - checkpoint == 20: dependency_analysisがrunning
 * - checkpoint == 40: dependency_analysis完了、er_diagramがrunning
 * - checkpoint == 50: er_diagram完了、requirements_analysisがrunning
 * - checkpoint >= 100またはcompleted: 全て完了
 */
const getInitialPhaseStates = (checkpoint?: number): PhaseStates => {
  if (!checkpoint || checkpoint < 20) {
    return {
      dependency_analysis: "pending",
      er_diagram: "pending",
      requirements_analysis: "pending",
    };
  }

  if (checkpoint === 20) {
    return {
      dependency_analysis: "running",
      er_diagram: "pending",
      requirements_analysis: "pending",
    };
  }

  if (checkpoint === 40) {
    return {
      dependency_analysis: "completed",
      er_diagram: "running",
      requirements_analysis: "pending",
    };
  }

  if (checkpoint === 50) {
    return {
      dependency_analysis: "completed",
      er_diagram: "completed",
      requirements_analysis: "running",
    };
  }

  // checkpoint >= 100 などの場合は全て完了
  return {
    dependency_analysis: "completed",
    er_diagram: "completed",
    requirements_analysis: "completed",
  };
};

const ReportCreationStep: React.FC<ReportCreationStepProps> = ({
  selectedApps,
  dfInstanceId,
  onCompletionAvailabilityChange,
  onClose,
  initialCheckpoint,
  organizationId,
}) => {
  const { refresh: refreshOrganization } = useOrganization(organizationId);
  const initialPhaseStates = getInitialPhaseStates(initialCheckpoint);
  const [logs, setLogs] = useState<LogEntry[]>(() => {
    const baseLogs = [
      { time: new Date().toLocaleTimeString(), message: "レポート作成を開始しました" },
      {
        time: new Date().toLocaleTimeString(),
        message: `${selectedApps.length}個のアプリを分析対象として設定`,
      },
      { time: new Date().toLocaleTimeString(), message: "分析処理を実行中..." },
    ];
    // 途中から再開した場合のログを追加
    if (initialCheckpoint && initialCheckpoint >= 20) {
      if (initialPhaseStates.dependency_analysis === "completed") {
        baseLogs.push({
          time: new Date().toLocaleTimeString(),
          message: "依存関係分析が完了しました",
        });
      }
      if (initialPhaseStates.er_diagram === "completed") {
        baseLogs.push({ time: new Date().toLocaleTimeString(), message: "ER図生成が完了しました" });
      }
      if (initialPhaseStates.requirements_analysis === "completed") {
        baseLogs.push({ time: new Date().toLocaleTimeString(), message: "要件分析が完了しました" });
      }
    }
    return baseLogs;
  });
  // initialCheckpointから初期状態を計算し、WebPubSubイベントで状態遷移
  const [phaseStates, setPhaseStates] = useState<PhaseStates>(initialPhaseStates);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [allTasksCompleted, setAllTasksCompleted] = useState<boolean>(false);

  const startTime = useRef<number>(Date.now());
  const hasInitialized = useRef(false);
  const elapsedTimerRef = useRef<NodeJS.Timeout | null>(null);

  const addLog = useCallback((message: string) => {
    setLogs((prev) => [
      ...prev,
      {
        time: new Date().toLocaleTimeString(),
        message,
      },
    ]);
  }, []);

  const handleStatusChange = useCallback(
    (status: ReverseEngineeringPhaseStatus) => {
      const { phase, status: phaseStatus } = status;

      setPhaseStates((prev) => {
        const newState = { ...prev };

        if (phaseStatus === "running" && prev[phase] === "pending") {
          newState[phase] = "running";
          addLog(`${PHASE_NAMES[phase]}を開始しました`);
        } else if (phaseStatus === "succeeded" && prev[phase] !== "completed") {
          newState[phase] = "completed";
          addLog(`${PHASE_NAMES[phase]}が完了しました`);
        } else if (phaseStatus === "failed" && prev[phase] !== "failed") {
          newState[phase] = "failed";
          addLog(`エラー: ${PHASE_NAMES[phase]}が失敗しました`);
          // フェーズ失敗時はtoast表示してモーダルを閉じる
          toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.STEP_PROCESSING_FAILED);
          refreshOrganization();
          onClose?.();
        }

        return newState;
      });
    },
    [addLog, onClose, refreshOrganization],
  );

  const handleConnectionError = useCallback(
    (error: Error) => {
      console.error("WebPubSub connection error:", error);
      addLog("エラー: 接続エラーが発生しました。");
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.STEP_PROCESSING_FAILED);
      refreshOrganization();
      onClose?.();
    },
    [addLog, onClose, refreshOrganization],
  );

  const { isConnected, error: wsError } = useReverseEngineeringStatus(
    dfInstanceId ?? null,
    handleStatusChange,
    {
      onError: handleConnectionError,
    },
  );

  // Check for all tasks completion
  useEffect(() => {
    const allCompleted =
      phaseStates.dependency_analysis === "completed" &&
      phaseStates.er_diagram === "completed" &&
      phaseStates.requirements_analysis === "completed";

    if (allCompleted && !allTasksCompleted) {
      addLog("すべての分析が完了しました");
      setAllTasksCompleted(true);
      onCompletionAvailabilityChange?.(true);
    }
  }, [phaseStates, allTasksCompleted, addLog, onCompletionAvailabilityChange]);

  // Initialize and start elapsed time counter
  useEffect(() => {
    if (!dfInstanceId || hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;
    startTime.current = Date.now();

    // Start elapsed time counter
    elapsedTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime.current) / 1000);
      setElapsedTime(elapsed);
    }, 1000);

    return () => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
      }
    };
  }, [dfInstanceId]);

  // Stop timer when completed
  useEffect(() => {
    if (allTasksCompleted) {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
      }
    }
  }, [allTasksCompleted]);

  // Handle WebSocket error
  useEffect(() => {
    if (wsError) {
      console.error("WebSocket error:", wsError);
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.STEP_PROCESSING_FAILED);
      refreshOrganization();
      onClose?.();
    }
  }, [wsError, onClose, refreshOrganization]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const calculateProgress = () => {
    if (allTasksCompleted) {
      return 100;
    }

    let progress = 0;
    const phases: (keyof PhaseStates)[] = [
      "dependency_analysis",
      "er_diagram",
      "requirements_analysis",
    ];

    for (const phase of phases) {
      if (phaseStates[phase] === "completed") {
        progress += PHASE_CHECKPOINTS[phase];
      } else if (phaseStates[phase] === "running") {
        // Give partial progress for running phase
        progress += PHASE_CHECKPOINTS[phase] * 0.5;
      }
    }

    return Math.round(Math.min(progress, 99));
  };

  const getStatusText = () => {
    if (allTasksCompleted) {
      return "分析完了";
    }
    return "レポート作成中...";
  };

  return (
    <div className={styles["analysisContainer"]}>
      <div className={styles["progressSection"]}>
        <div className={styles["progressInfo"]}>
          <span className={styles["progressText"]}>{getStatusText()}</span>
          <p className={styles["subText"]}>
            分析処理を実行しています。しばらくお待ちください。
            {elapsedTime > 0 && ` (経過時間: ${formatTime(elapsedTime)})`}
          </p>
          {!isConnected && !allTasksCompleted && (
            <p className={styles["connectionStatus"]}>接続中...</p>
          )}
          {elapsedTime > 240 && !allTasksCompleted && (
            <p className={styles["warningText"]}>
              {STATIC_PAGE_MESSAGES.REVERSE_ENGINEERING_PROCESSING_DELAY_WARNING}
            </p>
          )}
        </div>
        <div className={styles["loadingIndicator"]}>
          {!allTasksCompleted && <div className={styles["spinner"]} />}
        </div>
      </div>

      <div className={styles["progressBarSection"]}>
        <ProgressBar
          progress={calculateProgress()}
          showPercentage={true}
          className={styles["customProgressBar"]}
        />
      </div>

      <div className={styles["logSection"]}>
        <h3 className={styles["logTitle"]}>処理状況</h3>
        <div className={styles["logContent"]}>
          {logs.map((log) => (
            <div key={`${log.time}-${log.message}`} className={styles["logEntry"]}>
              <span className={styles["logTime"]}>{log.time}</span>
              <span className={styles["logMessage"]}>{log.message}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportCreationStep;
